/**
 * Envelope — the wire and storage format for every signed record in the system.
 *
 * Every message, every contribution, every audit-trail entry is wrapped in an
 * envelope before it touches the network or persistent storage. The envelope
 * provides:
 *
 *   - Authenticity: Ed25519 signature over canonical payload
 *   - Integrity: parent-hash chain makes tampering detectable
 *   - Ordering: lamport timestamp + parent hash gives total order within stream
 *   - Versioning: schema_version + funnel_version travel with the record
 *
 * Confidentiality is NOT provided by the envelope itself — it's added by the
 * group encryption layer (MLS) wrapping the payload before it's set here.
 * The envelope is the signed wrapper; what's inside may be plaintext or
 * MLS-ciphertext depending on the channel.
 */

import { blake3 } from '@noble/hashes/blake3';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils';
import type { Context, SignerIdentity } from '../identity';

export interface EnvelopeHeader {
  /** Schema version of the envelope format itself. */
  schemaVersion: string;
  /** Identifier of the funnel that produced this envelope (if any). */
  funnelVersion?: string;
  /** Environment context this envelope was signed under. */
  context: Context;
  /** ISO-8601 timestamp signed as part of the envelope. */
  timestamp: string;
  /** Lamport counter for ordering within a stream. */
  lamport: number;
  /** Hash of parent envelope in this stream — empty for stream root. */
  parentHash: string;
  /** Random nonce or zk-nullifier (anon contexts). */
  nonce: string;
  /** Public key of signer (or anon credential nullifier). */
  signerPubkey: string;
  /** Application content-type hint (e.g., "chat/message", "exchange/order"). */
  contentType: string;
}

export interface SignedEnvelope {
  header: EnvelopeHeader;
  /** Base64 of the payload (may be plaintext JSON or MLS ciphertext). */
  payload: string;
  /** Hex Ed25519 signature over canonical(header) || payload. */
  signature: string;
  /** Cached hash of this envelope. Verifiers MUST recompute. */
  hash: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CANONICALIZATION
// ─────────────────────────────────────────────────────────────────────────────
// We sign over a stable byte representation of the header. JSON.stringify is
// NOT canonical (key order, whitespace), so we use a sorted-key canonical
// JSON form. For alpha this is a simple impl; for beta we may swap to CBOR
// (Concise Binary Object Representation) for performance and stricter form.

function canonicalJSON(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map(canonicalJSON).join(',') + ']';
  }
  const keys = Object.keys(value as object).sort();
  const pairs = keys.map((k) => {
    const v = (value as Record<string, unknown>)[k];
    return v === undefined ? '' : JSON.stringify(k) + ':' + canonicalJSON(v);
  }).filter(Boolean);
  return '{' + pairs.join(',') + '}';
}

function hashEnvelope(header: EnvelopeHeader, payload: string): string {
  const bytes = utf8ToBytes(canonicalJSON(header) + '|' + payload);
  return bytesToHex(blake3(bytes));
}

// ─────────────────────────────────────────────────────────────────────────────
// SIGNING + VERIFICATION
// ─────────────────────────────────────────────────────────────────────────────

export interface SignOptions {
  identity: SignerIdentity;
  context: Context;
  contentType: string;
  payload: string;             // already base64 if binary
  parentHash?: string;
  lamport?: number;
  funnelVersion?: string;
}

export const ENVELOPE_SCHEMA_VERSION = '0.1.0';

export async function signEnvelope(opts: SignOptions): Promise<SignedEnvelope> {
  const pubkey = await opts.identity.pubkey(opts.context);
  const header: EnvelopeHeader = {
    schemaVersion: ENVELOPE_SCHEMA_VERSION,
    ...(opts.funnelVersion !== undefined && { funnelVersion: opts.funnelVersion }),
    context: opts.context,
    timestamp: new Date().toISOString(),
    lamport: opts.lamport ?? 0,
    parentHash: opts.parentHash ?? '',
    nonce: bytesToHex(crypto.getRandomValues(new Uint8Array(16))),
    signerPubkey: bytesToHex(pubkey),
    contentType: opts.contentType,
  };
  const signingInput = utf8ToBytes(canonicalJSON(header) + '|' + opts.payload);
  const signature = await opts.identity.sign(signingInput, opts.context);
  const hash = hashEnvelope(header, opts.payload);
  return {
    header,
    payload: opts.payload,
    signature: bytesToHex(signature),
    hash,
  };
}

export async function verifyEnvelope(
  envelope: SignedEnvelope,
  identity: SignerIdentity,
): Promise<{ ok: boolean; reason?: string }> {
  // 1. Recompute hash
  const recomputed = hashEnvelope(envelope.header, envelope.payload);
  if (recomputed !== envelope.hash) {
    return { ok: false, reason: 'hash mismatch' };
  }
  // 2. Recompute signing input and verify
  const signingInput = utf8ToBytes(canonicalJSON(envelope.header) + '|' + envelope.payload);
  const sigBytes = hexToBytes(envelope.signature);
  const pubkeyBytes = hexToBytes(envelope.header.signerPubkey);
  const ok = await identity.verify(signingInput, sigBytes, envelope.header.context, pubkeyBytes);
  if (!ok) {
    return { ok: false, reason: 'signature invalid' };
  }
  return { ok: true };
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// STREAM VALIDATION
// ─────────────────────────────────────────────────────────────────────────────
// A stream is a hash-chained sequence of envelopes. Validating a stream means
// checking each envelope is well-signed AND its parentHash matches the
// previous envelope's hash. Lamport counters must be strictly increasing.

export async function validateStream(
  envelopes: SignedEnvelope[],
  identity: SignerIdentity,
): Promise<{ ok: boolean; brokenAt?: number; reason?: string }> {
  let prevHash = '';
  let prevLamport = -1;
  for (let i = 0; i < envelopes.length; i++) {
    const env = envelopes[i]!;
    if (env.header.parentHash !== prevHash) {
      return { ok: false, brokenAt: i, reason: 'parent hash mismatch' };
    }
    if (env.header.lamport <= prevLamport) {
      return { ok: false, brokenAt: i, reason: 'lamport not increasing' };
    }
    const sigCheck = await verifyEnvelope(env, identity);
    if (!sigCheck.ok) {
      return { ok: false, brokenAt: i, reason: sigCheck.reason ?? 'signature check failed' };
    }
    prevHash = env.hash;
    prevLamport = env.header.lamport;
  }
  return { ok: true };
}
